/**
 * Type definitions for the Document Chat feature.
 * Provides type safety for error handling, streaming events, and chat state.
 */

/**
 * Represents an error that can occur during chat operations.
 * Used for consistent error handling across the chat feature.
 */
export interface ChatError {
  /** Human-readable error message */
  message: string;
  /** Optional error code for programmatic handling */
  code?: string;
  /** Optional stack trace for debugging */
  stack?: string;
}

/**
 * Type guard to check if an unknown value is a ChatError.
 *
 * @param error - The value to check
 * @returns True if the value is a ChatError
 *
 * @example
 * if (isChatError(error)) {
 *   console.error(error.message);
 * }
 */
export function isChatError(error: unknown): error is ChatError {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as ChatError).message === "string"
  );
}

/**
 * Converts any error value to a ChatError for consistent handling.
 *
 * @param error - The error value to convert
 * @returns A ChatError object
 *
 * @example
 * try {
 *   // ... some operation
 * } catch (e) {
 *   const chatError = toChatError(e);
 *   console.error(chatError.message);
 * }
 */
export function toChatError(error: unknown): ChatError {
  if (isChatError(error)) {
    return error;
  }

  if (error instanceof Error) {
    return {
      message: error.message,
      stack: error.stack,
    };
  }

  if (typeof error === "string") {
    return { message: error };
  }

  return { message: "An unknown error occurred" };
}

/**
 * Formats a ChatError for display to the user.
 *
 * @param error - The error to format
 * @returns A user-friendly error message
 *
 * @example
 * const errorMessage = formatErrorMessage(error);
 * // Returns "Unable to connect to AI service"
 */
export function formatErrorMessage(error: unknown): string {
  const chatError = toChatError(error);
  return chatError.message;
}
